import React, { useState, useCallback } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import Cropper from 'react-easy-crop';
import {
  Button,
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { SliderFilledTrack, SliderTrack, SliderThumb, SliderRoot } from '@/components/ui/chakra-v2-compat';
import { FaSearchPlus, FaSearchMinus, FaUndo, FaCrop } from 'react-icons/fa';

// Función para crear la imagen recortada
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  // Fondo blanco: PNG con transparencia → JPEG no debe quedar negro
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, safeArea, safeArea);

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(URL.createObjectURL(blob));
    }, 'image/jpeg', 0.9);
  });
};

const ImageCropper = ({
  isOpen,
  onClose,
  imageSrc,
  onCropComplete,
  aspectRatio = 1, // 1 para cuadrado (avatar), 16/9 para fondo
  cropShape = 'rect', // 'rect' o 'round'
  title = 'Editar imagen',
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const bgColor = useColorModeValue('gray.100', 'gray.800');
  const sliderBg = useColorModeValue('gray.200', 'gray.600');

  const onCropChange = useCallback((location) => {
    setCrop(location);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const handleCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;

    setIsProcessing(true);
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      onCropComplete(croppedImage);
      handleClose();
    } catch (e) {
      console.error('Error cropping image:', e);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    onClose();
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  return (
    <Dialog.Root open={isOpen} size='xl' placement='center' onOpenChange={e => {
      if (!e.open) {
        handleClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.700" />
        <Dialog.Positioner>
          <Dialog.Content maxW="600px" mx={4}>
            <Dialog.Header>
              <HStack gap={3}>
                <Icon color="purple.500" asChild><FaCrop /></Icon>
                <Text>{title}</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />

            <Dialog.Body pb={4}>
              <VStack gap={6}>
                {/* Área de recorte */}
                <Box
                  position="relative"
                  w="full"
                  h="350px"
                  bg={bgColor}
                  rounded="md"
                  overflow="hidden"
                >
                  {imageSrc && (
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      rotation={rotation}
                      aspect={aspectRatio}
                      cropShape={cropShape}
                      onCropChange={onCropChange}
                      onZoomChange={onZoomChange}
                      onCropComplete={handleCropComplete}
                      showGrid={true}
                    />
                  )}
                </Box>

                {/* Controles */}
                <VStack gap={4} w="full" px={2} align="stretch">
                  {/* Control de zoom */}
                  <Box w="full">
                    <HStack justify="space-between" mb={2}>
                      <HStack gap={2}>
                        <Icon color="gray.500" asChild><FaSearchMinus /></Icon>
                        <Text fontSize="sm" fontWeight="600" color="gray.600">
                          Zoom
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color="gray.500" minW="50px" textAlign="right">
                        {Math.round(zoom * 100)}%
                      </Text>
                    </HStack>
                    <Box w="full" px={1}>
                      <SliderRoot
                        aria-label="Zoom de la imagen"
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        onValueChange={(v) => setZoom(Number(v) || 1)}
                        colorPalette="purple"
                        w="full"
                      >
                        <SliderTrack bg={sliderBg}>
                          <SliderFilledTrack bg="purple.500" />
                        </SliderTrack>
                        <SliderThumb boxSize={5} />
                      </SliderRoot>
                    </Box>
                    <HStack justify="flex-end" mt={1}>
                      <Icon color="gray.400" boxSize={3} asChild><FaSearchPlus /></Icon>
                    </HStack>
                  </Box>

                  {/* Control de rotación */}
                  <Box w="full">
                    <HStack justify="space-between" mb={2}>
                      <HStack gap={2}>
                        <Icon color="gray.500" asChild><FaUndo /></Icon>
                        <Text fontSize="sm" fontWeight="600" color="gray.600">
                          Rotación
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color="gray.500" minW="50px" textAlign="right">
                        {rotation}°
                      </Text>
                    </HStack>
                    <Box w="full" px={1}>
                      <SliderRoot
                        aria-label="Rotación de la imagen"
                        value={rotation}
                        min={-180}
                        max={180}
                        step={1}
                        onValueChange={(v) => setRotation(Number(v) || 0)}
                        colorPalette="purple"
                        w="full"
                      >
                        <SliderTrack bg={sliderBg}>
                          <SliderFilledTrack bg="purple.500" />
                        </SliderTrack>
                        <SliderThumb boxSize={5} />
                      </SliderRoot>
                    </Box>
                  </Box>
                </VStack>

                {/* Instrucciones */}
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  Arrastra para mover la imagen. Usa los controles para ajustar el zoom y la rotación.
                </Text>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="ghost" onClick={handleReset}>
                  Restablecer
                </Button>
                <Button variant="outline" onClick={handleClose}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={handleSave}
                  loading={isProcessing}
                  loadingText="Procesando..."
                >
                  Aplicar recorte
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
};

export default ImageCropper;

